import type { RouteRecordRaw } from "vue-router"
import type { RouteAccessContext } from "./permissionRoutes"

type SectionComponent = NonNullable<RouteRecordRaw["component"]>
type GroupSpec = { key: string; title: string; pages: [string, string][] }

export const storeSettingGroups: GroupSpec[] = [
  { key: "shop", title: "门店资料", pages: [["/setting/online-shop/shop-manage", "门店资料"]] },
  { key: "products", title: "商品管理", pages: [["/setting/product-manage", "商品档案"], ["/setting/other/unit-manage", "商品单位"], ["/setting/online-shop/phone-product", "推荐商品"]] },
  { key: "tables", title: "桌台与计费", pages: [["/setting/table-manage", "桌台管理"], ["/setting/billing-rule-manage", "计费规则"]] },
  { key: "checkout", title: "收银设置", pages: [["/setting/pay-method-manage/pay-method", "收款方式"], ["/setting/pay-method-manage/day-end", "交接班与日结"], ["/setting/other/discount-settings", "金额取整"]] },
  { key: "staff", title: "员工与权限", pages: [["/setting/worker-manage/worker-manage", "员工"], ["/setting/worker-manage/role-manage", "角色权限"], ["/setting/worker-manage/employee-commissions", "提成规则"], ["/setting/operation-center/operation-config", "操作审批规则"]] },
  { key: "devices", title: "设备与通知", pages: [["/setting/other/device-manage", "打印设备"], ["/setting/other/message-manage", "短信通知"], ["/setting/other/voice-broadcast-test", "语音播报"]] },
  { key: "online", title: "网订设置", pages: [["/setting/online-shop/shop-order", "网订规则与包间资料"]] },
]

function cloneRoutes(routes: RouteRecordRaw[]): RouteRecordRaw[] {
  return routes.map(route => ({ ...route, meta: { ...route.meta }, children: route.children ? cloneRoutes(route.children) : undefined }) as RouteRecordRaw)
}

function labelPage(route: RouteRecordRaw, title: string, activeMenu: string) {
  const oldTitle = route.meta?.title
  route.meta = { ...route.meta, title, activeMenu, searchAliases: [oldTitle].filter(Boolean) }
  for (const child of route.children || []) {
    child.meta = { ...child.meta, activeMenu }
    if (child.path.endsWith("/index")) child.meta.title = title
  }
}

function bindSections(pages: RouteRecordRaw[], component: SectionComponent) {
  pages.forEach(page => {
    if (!page.children?.length) return
    page.component = component
    page.props = { routeList: pages.filter(item => !item.meta?.hideInLevelTwoTabs) }
  })
}

function directory(path: string, title: string, mode: string, component: SectionComponent): RouteRecordRaw {
  return { path, name: `StoreDirectory-${mode}`, component, props: { mode }, meta: { title, activeMenu: path } }
}

/**
 * Presentation projection ONLY AFTER authorization. Never feed this tree back into permission filtering:
 * original ancestor gates remain enforced by filterAuthorizedRoutes/filterNavigationRoutes.
 * Page paths/names and legacy group redirects survive the move.
 */
export function organizeStoreRoutes(
  authorized: RouteRecordRaw[],
  context: RouteAccessContext,
  section: SectionComponent,
  catalog: SectionComponent,
): RouteRecordRaw[] {
  if (context.hqMenuEnabled || ["chain-headquarter", "chain-region"].includes(context.activeScopeType || "")) return authorized
  const routes = cloneRoutes(authorized)
  const settings = routes.find(route => route.path === "/setting")
  if (settings?.children?.length) {
    const originals = settings.children
    const candidates = originals.flatMap(route => [route, ...(route.children || [])])
    const consumed = new Set<string>()
    const groups: RouteRecordRaw[] = []
    for (const spec of storeSettingGroups) {
      const path = `/setting/group-${spec.key}`
      const pages = spec.pages.flatMap(([source, title]) => {
        const page = candidates.find(route => route.path === source)
        if (!page) return []
        consumed.add(source)
        const oldGroup = originals.find(group => group.children?.some(child => child.path === source))
        labelPage(page, title, path)
        page.meta = { ...page.meta, hidden: true, searchAliases: [...(page.meta?.searchAliases as string[] || []), oldGroup?.meta?.title].filter(Boolean) }
        return [page]
      })
      if (!pages.length) continue
      bindSections(pages, section)
      groups.push({ path, name: `StoreSettings-${spec.key}`, redirect: pages[0].path, meta: { title: spec.title, activeMenu: path }, children: pages })
    }
    // Preserve every old URL, including approvals and legacy category bookmarks.
    const legacy = originals.filter(route => !consumed.has(route.path)).map(route => {
      route.children = route.children?.filter(child => !consumed.has(child.path))
      route.meta = { ...route.meta, hidden: true, breadcrumb: false }
      if (route.children?.length) bindSections(route.children, section)
      else delete route.children
      return route
    })
    settings.children = [...groups, ...legacy]
    if (groups.length) settings.redirect = groups[0].path
  }
  const membership = routes.find(route => route.path === "/membership")
  const memberLabels: Record<string, string> = { "member-manage": "会员列表", credit: "积分", marketing: "储值规则", "member-level": "等级权益", "risk-warning": "风险客户" }
  membership?.children?.forEach(group => {
    const title = memberLabels[group.path.split("/").pop() || ""]
    if (title) group.meta = { ...group.meta, title, searchAliases: [group.meta?.title] }
    if (group.children) bindSections(group.children.filter(page => !page.path.includes(":pathMatch")), section)
  })
  const stock = routes.find(route => route.path === "/stock")
  if (stock?.children) {
    const manage = stock.children.find(route => route.path === "/stock/stock-manage")
    if (manage?.children?.length) {
      const tasks = manage.children
      const order = ["stock-stat", "stock-in", "stock-out", "check-record", "stock-adjustment"]
      tasks.sort((a, b) => order.indexOf(a.path.split("/").pop()!) - order.indexOf(b.path.split("/").pop()!))
      tasks.forEach(page => {
        const labels: Record<string, string> = { "stock-in": "入库", "stock-out": "出库", "check-record": "盘点" }
        labelPage(page, labels[page.path.split("/").pop()!] || String(page.meta?.title), page.path)
        page.meta = { ...page.meta, hidden: false }
        bindSections([page], section)
      })
      manage.children = undefined
      manage.meta = { ...manage.meta, hidden: true }
      stock.children = [...tasks.filter(page => !page.path.endsWith("stock-adjustment")), ...stock.children, ...tasks.filter(page => page.path.endsWith("stock-adjustment"))]
    }
    const reports = stock.children.find(route => route.path === "/stock/stock-dashboard")
    if (reports?.children?.length) {
      const catalogRoute = directory("/stock/stock-dashboard/catalog/index", "库存报表目录", "stock", catalog)
      catalogRoute.meta = { ...catalogRoute.meta, hidden: true, activeMenu: reports.path }
      reports.children.unshift(catalogRoute)
      reports.redirect = catalogRoute.path
      bindSections(reports.children, section)
    }
    const items = stock.children.find(route => route.path === "/stock/stock-setting")
    if (items) {
      items.meta = { ...items.meta, title: "物品与成本", searchAliases: ["基础设置"] }
      if (items.children) bindSections(items.children, section)
    }
  }
  const dashboard = routes.find(route => route.path === "/dashboard")
  if (dashboard?.children?.length) {
    dashboard.children.forEach(group => {
      if (group.children) bindSections(group.children.filter(page => !page.path.startsWith("/dashboard/sensitive-report/sensitive-approval")), section)
    })
    const catalogRoute = directory("/dashboard/catalog/index", "报表目录", "reports", catalog)
    dashboard.children.unshift(catalogRoute)
    dashboard.redirect = catalogRoute.path
  }
  return routes
}

export interface StoreDirectoryEntry { path: string; title: string; group: string; keywords: string }

/** Index real landing pages only; details, redirects and unavailable routes are excluded. */
export function getStoreDirectoryEntries(routes: RouteRecordRaw[]): StoreDirectoryEntry[] {
  const entries: StoreDirectoryEntry[] = []
  const visit = (route: RouteRecordRaw, parents: string[], inheritedAliases: string[]) => {
    if (route.path.startsWith("/hq") || route.meta?.locked || route.path.startsWith("/dashboard/sensitive-report/sensitive-approval") || route.path.startsWith("/setting/operation-center/operation-approval") || route.path === "/stock/stock-stat") return
    const title = typeof route.meta?.title === "string" ? route.meta.title : ""
    const labels = [...parents, title].filter(Boolean)
    const aliases = [...inheritedAliases, ...(Array.isArray(route.meta?.searchAliases) ? route.meta.searchAliases : [])]
    if (route.component && !route.redirect && route.path.endsWith("/index") && !route.path.includes("/catalog/") && title) {
      entries.push({ path: route.path, title, group: parents.filter((value, index) => value !== parents[index - 1] && (index !== parents.length - 1 || value !== title)).join(" / "), keywords: [...labels, ...aliases].join(" ") })
    }
    route.children?.forEach(child => visit(child, labels, aliases))
  }
  routes.filter(route => ["/business", "/reservation", "/order", "/membership", "/stock", "/dashboard", "/setting", "/dashboard/handover-report", "/operation-approval"].includes(route.path)).forEach(route => visit(route, [], []))
  return entries.filter((entry, index) => entries.findIndex(item => item.path === entry.path) === index)
}

export function isStoreReport(path: string) {
  return path.startsWith("/dashboard/") || path.startsWith("/membership/dashboard/") || path.startsWith("/stock/stock-dashboard/")
}

export function getStoreReportCategory(path: string): string {
  if (path.startsWith("/membership/dashboard/")) return "会员报表"
  if (path.startsWith("/stock/stock-dashboard/")) return "库存报表"
  const groups: Record<string, string> = {
    overview: "经营概览",
    "business-report": "营业与收款",
    "table-report": "桌台报表",
    "product-report": "商品报表",
    "commission-report": "提成报表",
    "reservation-report": "预订报表",
    "sensitive-report": "操作审计",
    "operation-record": "操作审计",
    "handover-report": "交接班",
  }
  return groups[path.split("/")[2]] || "其他报表"
}
